import { Channel } from '@/Enums/Channel.enum';
import Color from '@/Enums/Color.enum';
import { IImpressionCalculation } from '@/Interfaces/Forecaster';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import MetricInput from './MetricInput';

interface SpendCalculatorProps {
  forecasterData: Record<string, IImpressionCalculation> | null;
  currentChannel: Channel;
}

type CalculatorField = 'impressions' | 'cpm' | 'budget';
type FieldErrors = Partial<Record<CalculatorField, string>>;

const SpendCalculator = ({ forecasterData, currentChannel }: SpendCalculatorProps) => {
  const { control, setValue } = useFormContext();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<CalculatorField>>(new Set());
  const lastSyncedImpressions = useRef<number | null>(null);
  const manualEditRef = useRef(false);

  const calculator = useWatch({ control, name: 'calculator' }) ?? {
    impressions: null,
    cpm: null,
    budget: null,
  };

  const { impressions: calcImpressions, cpm: calcCpm, budget: calcBudget } = calculator;

  const channelData = forecasterData?.[currentChannel];
  const forecastedImpressions = channelData?.impressions ?? 0;

  useEffect(() => {
    const shouldSync =
      forecastedImpressions > 0 && forecastedImpressions !== lastSyncedImpressions.current && !manualEditRef.current;

    if (shouldSync) {
      setValue('calculator.impressions', forecastedImpressions);
      lastSyncedImpressions.current = forecastedImpressions;
    }
  }, [forecastedImpressions, setValue, touchedFields]);

  const hasImpressions = calcImpressions !== null && calcImpressions > 0;
  const hasCpm = calcCpm !== null && calcCpm > 0;
  const hasBudget = calcBudget !== null && calcBudget >= 0;

  const filledCount = [hasImpressions, hasCpm, hasBudget].filter(Boolean).length;

  const { calculatedField, calculatedValue } = useMemo(() => {
    if (filledCount !== 2) {
      return { calculatedField: null, calculatedValue: 0 };
    }

    if (hasImpressions && hasCpm) {
      return {
        calculatedField: 'budget' as CalculatorField,
        calculatedValue: (calcImpressions * calcCpm) / 1000,
      };
    }

    if (hasImpressions && hasBudget) {
      if (calcImpressions === 0) {
        return { calculatedField: null, calculatedValue: 0 };
      }
      return {
        calculatedField: 'cpm' as CalculatorField,
        calculatedValue: (calcBudget * 1000) / calcImpressions,
      };
    }

    if (hasCpm && hasBudget) {
      if (calcCpm === 0) {
        return { calculatedField: null, calculatedValue: 0 };
      }
      return {
        calculatedField: 'impressions' as CalculatorField,
        calculatedValue: (calcBudget * 1000) / calcCpm,
      };
    }

    return { calculatedField: null, calculatedValue: 0 };
  }, [filledCount, hasImpressions, hasCpm, hasBudget, calcImpressions, calcCpm, calcBudget]);

  useEffect(() => {
    const newErrors: FieldErrors = {};

    if (calcImpressions === 0 && touchedFields.has('impressions')) {
      newErrors.impressions = 'Impressions must be greater than 0.';
    }

    if (calcCpm === 0 && touchedFields.has('cpm')) {
      newErrors.cpm = 'CPM must be greater than 0.';
    }

    setErrors(newErrors);
  }, [calcImpressions, calcCpm, touchedFields]);

  const handleFieldChange = useCallback(
    (field: CalculatorField) => (eventOrValue: string | { target: { value: string } }) => {
      const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
      const parsedValue = value === '' ? null : parseFloat(value) || 0;
      setValue(`calculator.${field}`, parsedValue);
      setTouchedFields(prev => new Set(prev).add(field));
    },
    [setValue],
  );

  const handleClearField = useCallback(
    (field: CalculatorField) => () => {
      setValue(`calculator.${field}`, null);
    },
    [setValue],
  );

  const getFieldValue = useCallback(
    (field: CalculatorField): string => {
      if (calculatedField === field) {
        if (field === 'budget' || field === 'cpm') {
          return numberToUsFormat(calculatedValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return numberToUsFormat(Math.round(calculatedValue));
      }

      const values: Record<CalculatorField, number | null> = {
        impressions: calcImpressions,
        cpm: calcCpm,
        budget: calcBudget,
      };

      return values[field]?.toString() ?? '';
    },
    [calculatedField, calculatedValue, calcImpressions, calcCpm, calcBudget],
  );

  const handleBlur = useCallback(() => {
    if (calcImpressions !== lastSyncedImpressions.current) {
      manualEditRef.current = true;
    }
  }, [calcImpressions]);

  return (
    <Paper className='flex flex-col justify-start items-center px-3 py-4 gap-3 shadow-md-lg'>
      <h2 className='text-lg font-semibold w-full' style={{ color: Color.TextDark }}>
        Budget Calculator
      </h2>

      <div className='flex flex-col gap-3 w-full'>
        <MetricInput
          field='impressions'
          label='Impressions'
          hasValue={hasImpressions}
          value={getFieldValue('impressions')}
          isCalculated={calculatedField === 'impressions'}
          showClearButton={!!calculatedField}
          error={errors.impressions}
          placeholder='Enter impressions'
          onChange={handleFieldChange('impressions')}
          onClear={handleClearField('impressions')}
          onBlur={handleBlur}
        />
        <MetricInput
          field='cpm'
          label='CPM ($)'
          hasValue={hasCpm}
          value={getFieldValue('cpm')}
          isCalculated={calculatedField === 'cpm'}
          showClearButton={!!calculatedField}
          error={errors.cpm}
          showDollarSign
          fractionDigits={2}
          placeholder='Enter CPM'
          onChange={handleFieldChange('cpm')}
          onClear={handleClearField('cpm')}
        />
        <MetricInput
          field='budget'
          label='Budget ($)'
          hasValue={hasBudget}
          value={getFieldValue('budget')}
          isCalculated={calculatedField === 'budget'}
          showClearButton={!!calculatedField}
          showDollarSign
          fractionDigits={2}
          placeholder='Enter budget'
          onChange={handleFieldChange('budget')}
          onClear={handleClearField('budget')}
        />

        <p className='text-xs italic' style={{ color: Color.Grey }}>
          Formula: Impressions x CPM / 1000 = Budget
        </p>
      </div>
    </Paper>
  );
};

export default SpendCalculator;
